import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Guid } from 'guid-typescript';
import { ObservationContextService } from '../../copilot/services/observation-context.service';
import { SQA_RUN_KEY_PATTERN, sqaRunKey } from '../services/sqa-run-mode';

/**
 * Originates a fresh tracking id (a GUID) on every outbound HttpClient request
 * — commands, queries, GraphQL, plain HTTP — and carries it as a W3C `baggage`
 * header: `baggage: TrackingId=<id>`. A new GUID is minted per `intercept`
 * call, so each request (including a redirect/continuation) originates its own
 * tracing id rather than re-firing a leaked one.
 *
 * Baggage (not `traceparent`) is deliberate: the frontend emits no spans, so a
 * `traceparent` would orphan the API's root span, whereas baggage carries no
 * parent-child semantics. On the backend, .NET 10 auto-populates
 * `Activity.Baggage` from this header (span tagging) and the edge middleware
 * parses the same header for the app field. The `TrackingId` header is no
 * longer emitted — baggage is the sole transport.
 *
 * The key is `TrackingId` (PascalCase) and must match the backend exactly;
 * baggage keys are case-sensitive. The value is a canonical GUID (hex + hyphens,
 * the "D" form), which is already a valid W3C baggage value — no encoding needed.
 *
 * WHILE AN OBSERVATION IS CURRENT a second list member rides the same header:
 * `baggage: TrackingId=<hop>,ObservationId=<execution>` (MISN-E-71-01, ADR 0070).
 * That is what puts the commands the user fires BY HAND — on a screen Mission
 * sent them to — into the same trail as the ones the runtime ran on the server.
 * The same header rather than one of its own, because the edge already extracts
 * baggage with the runtime's own W3C parser and reads both keys off that one
 * parse; and appended rather than prepended so an existing reader of
 * `TrackingId=…` at the head of the value is unaffected.
 *
 * Nothing is added when no Observation is current, which is the ordinary case:
 * the header stays byte-for-byte what it was, and the backend leaves its
 * ObservationId null.
 *
 * IN SQA RUN MODE (`?sqa-run=SQA-RUN-NNNNN`; ITH-E-09-03, Plan ITH-P-01) one more member rides LAST:
 * `baggage: TrackingId=<hop>[,ObservationId=<execution>],SqaRun=<runKey>`. The edge lifts it onto the
 * DistributedRequestContext, so every log line and span the run's requests cause — including the lines
 * logged before the run's company exists — can be found by the run key. Last, for the same reason
 * ObservationId is appended: a reader of the head is unaffected. A normal session has no key, so its
 * header stays byte-for-byte what it was.
 */
@Injectable()
export class TrackingIdInterceptor implements HttpInterceptor {
  constructor(private readonly observationContext: ObservationContextService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const id = Guid.create().toString();
    // Read synchronously — an interceptor cannot await, and the id is a plain value the copilot
    // layer has already put there.
    const observationId = this.observationContext?.current;
    let baggage = observationId
      ? `TrackingId=${id},ObservationId=${observationId}`
      : `TrackingId=${id}`;
    const runKey = sqaRunKey();
    if (runKey && SQA_RUN_KEY_PATTERN.test(runKey)) {
      baggage += `,SqaRun=${runKey}`;
    }
    const cloned = req.clone({ setHeaders: { baggage } });
    return next.handle(cloned);
  }
}
